/**
 * Supplier Orders Page
 *
 * View and manage orders sent to a specific supplier.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  ArrowLeft,
  Truck,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  MoreVertical,
  Eye,
  Edit,
  Send,
  RefreshCw,
} from 'lucide-react';

interface SupplierOrder {
  id: string;
  supplier_order_number: string | null;
  internal_reference: string | null;
  status: string;
  submitted_at: string | null;
  submitted_via: string | null;
  subtotal: number;
  shipping_cost: number;
  total_cost: number;
  currency: string;
  payment_status: string;
  tracking_number: string | null;
  tracking_url: string | null;
  carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  error_message: string | null;
  created_at: string;
  customer_order: {
    id: string;
    order_number: string;
    customer_email: string;
    customer_name: string;
  };
}

interface Supplier {
  id: string;
  name: string;
}

const STATUS_CONFIG: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <Clock className="w-4 h-4" /> },
  submitted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Send className="w-4 h-4" /> },
  confirmed: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: <CheckCircle className="w-4 h-4" /> },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <RefreshCw className="w-4 h-4" /> },
  shipped: { bg: 'bg-purple-100', text: 'text-purple-800', icon: <Truck className="w-4 h-4" /> },
  delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: <XCircle className="w-4 h-4" /> },
  failed: { bg: 'bg-red-100', text: 'text-red-800', icon: <AlertCircle className="w-4 h-4" /> },
};

export default function SupplierOrdersPage() {
  const { t } = useTranslation('store');
  const { storeId, supplierId } = useParams<{ storeId: string; supplierId: string }>();

  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState<SupplierOrder | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Update status modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [updatingOrder, setUpdatingOrder] = useState<SupplierOrder | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const loadOrders = useCallback(async () => {
    if (!storeId || !supplierId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const response = await api.get(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/orders?${params}`);
      const data = await response.json() as { supplier_orders: SupplierOrder[]; supplier: Supplier };
      setOrders(data.supplier_orders || []);
      setSupplier(data.supplier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [storeId, supplierId, search, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleUpdateStatus = async () => {
    if (!storeId || !supplierId || !updatingOrder) return;

    try {
      setSaving(true);
      await api.put(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/orders/${updatingOrder.id}`, {
        status: newStatus,
        tracking_number: trackingNumber || null,
        tracking_url: trackingUrl || null,
      });
      setShowStatusModal(false);
      setUpdatingOrder(null);
      setNewStatus('');
      setTrackingNumber('');
      setTrackingUrl('');
      setSuccess(t('suppliers.orderUpdated', 'Order updated successfully'));
      loadOrders();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!storeId || !supplierId) return;
    if (!confirm(t('suppliers.confirmCancelOrder', 'Are you sure you want to cancel this order?'))) return;

    try {
      await api.delete(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/orders/${orderId}`);
      setSuccess(t('suppliers.orderCancelled', 'Order cancelled'));
      loadOrders();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel order');
    }
  };

  const formatCurrency = (cents: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openUpdateModal = (order: SupplierOrder) => {
    setUpdatingOrder(order);
    setNewStatus(order.status);
    setTrackingNumber(order.tracking_number || '');
    setTrackingUrl(order.tracking_url || '');
    setShowStatusModal(true);
    setOpenMenu(null);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={`/store/${storeId}/suppliers/${supplierId}`}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-6 h-6" />
              {supplier?.name} - {t('suppliers.orders', 'Orders')}
            </h1>
            <p className="text-gray-500">
              {t('suppliers.manageSupplierOrders', 'Manage orders sent to this supplier')}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('suppliers.searchOrders', 'Search orders...')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('suppliers.allStatuses', 'All Statuses')}</option>
          <option value="pending">{t('suppliers.pending', 'Pending')}</option>
          <option value="submitted">{t('suppliers.submitted', 'Submitted')}</option>
          <option value="confirmed">{t('suppliers.confirmed', 'Confirmed')}</option>
          <option value="processing">{t('suppliers.processing', 'Processing')}</option>
          <option value="shipped">{t('suppliers.shipped', 'Shipped')}</option>
          <option value="delivered">{t('suppliers.delivered', 'Delivered')}</option>
          <option value="cancelled">{t('suppliers.cancelled', 'Cancelled')}</option>
          <option value="failed">{t('suppliers.failed', 'Failed')}</option>
        </select>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('suppliers.noOrders', 'No orders yet')}
          </h3>
          <p className="text-gray-500">
            {t('suppliers.noOrdersDescription', 'Orders will appear here when customer orders are fulfilled through this supplier')}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.order', 'Order')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.customerOrder', 'Customer Order')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.status', 'Status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.total', 'Total')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.tracking', 'Tracking')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.date', 'Date')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('suppliers.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => {
                const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;

                return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {order.internal_reference || order.id.slice(0, 8)}
                      </div>
                      {order.supplier_order_number && (
                        <div className="text-sm text-gray-500">
                          Supplier: {order.supplier_order_number}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/store/${storeId}/orders/${order.customer_order.id}`}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        #{order.customer_order.order_number}
                      </Link>
                      <div className="text-sm text-gray-500">{order.customer_order.customer_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        {statusConfig.icon}
                        {order.status}
                      </span>
                      {order.error_message && (
                        <div className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={order.error_message}>
                          {order.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium">{formatCurrency(order.total_cost, order.currency)}</div>
                      <div className="text-xs text-gray-500">
                        {formatCurrency(order.subtotal, order.currency)} + {formatCurrency(order.shipping_cost, order.currency)} ship
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {order.tracking_number ? (
                        <div>
                          {order.tracking_url ? (
                            <a
                              href={order.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              {order.tracking_number}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-gray-900">{order.tracking_number}</span>
                          )}
                          {order.carrier && (
                            <div className="text-xs text-gray-500">{order.carrier}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenu(openMenu === order.id ? null : order.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-500" />
                        </button>
                        {openMenu === order.id && (
                          <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowDetailModal(true);
                                setOpenMenu(null);
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                            >
                              <Eye className="w-4 h-4" />
                              {t('suppliers.viewDetails', 'View Details')}
                            </button>
                            <button
                              onClick={() => openUpdateModal(order)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                            >
                              <Edit className="w-4 h-4" />
                              {t('suppliers.updateStatus', 'Update Status')}
                            </button>
                            {['pending', 'submitted'].includes(order.status) && (
                              <>
                                <hr className="my-1" />
                                <button
                                  onClick={() => {
                                    handleCancelOrder(order.id);
                                    setOpenMenu(null);
                                  }}
                                  className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                >
                                  <XCircle className="w-4 h-4" />
                                  {t('suppliers.cancelOrder', 'Cancel Order')}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      {orders.length > 0 && (
        <div className="mt-6 grid grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold">{orders.length}</div>
            <div className="text-sm text-gray-500">{t('suppliers.totalOrders', 'Total Orders')}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {orders.filter(o => ['pending', 'submitted', 'confirmed', 'processing'].includes(o.status)).length}
            </div>
            <div className="text-sm text-gray-500">{t('suppliers.inProgress', 'In Progress')}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {orders.filter(o => o.status === 'delivered').length}
            </div>
            <div className="text-sm text-gray-500">{t('suppliers.delivered', 'Delivered')}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold">
              {formatCurrency(orders.reduce((sum, o) => sum + o.total_cost, 0))}
            </div>
            <div className="text-sm text-gray-500">{t('suppliers.totalSpent', 'Total Spent')}</div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && updatingOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">{t('suppliers.updateOrder', 'Update Order')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.status', 'Status')}
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">{t('suppliers.pending', 'Pending')}</option>
                  <option value="submitted">{t('suppliers.submitted', 'Submitted')}</option>
                  <option value="confirmed">{t('suppliers.confirmed', 'Confirmed')}</option>
                  <option value="processing">{t('suppliers.processing', 'Processing')}</option>
                  <option value="shipped">{t('suppliers.shipped', 'Shipped')}</option>
                  <option value="delivered">{t('suppliers.delivered', 'Delivered')}</option>
                  <option value="cancelled">{t('suppliers.cancelled', 'Cancelled')}</option>
                  <option value="failed">{t('suppliers.failed', 'Failed')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.trackingNumber', 'Tracking Number')}
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 1Z999AA10123456784"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.trackingUrl', 'Tracking URL')}
                </label>
                <input
                  type="url"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setUpdatingOrder(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {t('suppliers.orderDetails', 'Order Details')} - {selectedOrder.internal_reference}
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">{t('suppliers.status', 'Status')}</label>
                  <div className="font-medium capitalize">{selectedOrder.status}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">{t('suppliers.paymentStatus', 'Payment Status')}</label>
                  <div className="font-medium capitalize">{selectedOrder.payment_status}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">{t('suppliers.total', 'Total')}</label>
                  <div className="font-medium">{formatCurrency(selectedOrder.total_cost, selectedOrder.currency)}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">{t('suppliers.created', 'Created')}</label>
                  <div className="font-medium">{formatDate(selectedOrder.created_at)}</div>
                </div>
                {selectedOrder.submitted_at && (
                  <div>
                    <label className="text-sm text-gray-500">{t('suppliers.submittedAt', 'Submitted At')}</label>
                    <div className="font-medium">{formatDate(selectedOrder.submitted_at)}</div>
                  </div>
                )}
                {selectedOrder.shipped_at && (
                  <div>
                    <label className="text-sm text-gray-500">{t('suppliers.shippedAt', 'Shipped At')}</label>
                    <div className="font-medium">{formatDate(selectedOrder.shipped_at)}</div>
                  </div>
                )}
              </div>

              {selectedOrder.tracking_number && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="text-sm text-gray-500">{t('suppliers.tracking', 'Tracking')}</label>
                  <div className="font-medium">
                    {selectedOrder.tracking_url ? (
                      <a href={selectedOrder.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        {selectedOrder.tracking_number}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      selectedOrder.tracking_number
                    )}
                  </div>
                  {selectedOrder.carrier && (
                    <div className="text-sm text-gray-500 mt-1">Carrier: {selectedOrder.carrier}</div>
                  )}
                </div>
              )}

              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="text-sm text-gray-500">{t('suppliers.customerOrder', 'Customer Order')}</label>
                <Link
                  to={`/store/${storeId}/orders/${selectedOrder.customer_order.id}`}
                  className="block font-medium text-blue-600 hover:text-blue-700"
                >
                  #{selectedOrder.customer_order.order_number}
                </Link>
                <div className="text-sm text-gray-600">
                  {selectedOrder.customer_order.customer_name} ({selectedOrder.customer_order.customer_email})
                </div>
              </div>

              {selectedOrder.error_message && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <label className="text-sm text-red-600 font-medium">{t('suppliers.error', 'Error')}</label>
                  <div className="text-red-700">{selectedOrder.error_message}</div>
                </div>
              )}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedOrder(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {openMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setOpenMenu(null)} />
      )}
    </div>
  );
}
